/**
 * Reiter "Neue Bestellungen" (Migration 0070, Recht "orders.manage", ab Werk
 * President/VizePresident/Phantom/Sgt. at Arms): alle eingegangenen, noch
 * offenen Bestellungen komplett einsehen und abschließen. Eine abgeschlossene
 * Bestellung wird dabei gelöscht (Migration 0071) und verschwindet sofort aus
 * dieser Ansicht (und aus der Liste des Bestellers). Die Liste aktualisiert
 * sich alle 60 Sekunden automatisch (und über "Aktualisieren"), damit neue
 * Bestellungen ohne Neuladen auftauchen.
 */
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { Card } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import { formatDateTime } from "../../utils/formatDate";
import { formatPrice } from "./OrdersPage";
import { notifyOpenOrdersChanged } from "../../hooks/useOpenOrdersCount";
import type { Order } from "../../types";

export default function NewOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completingId, setCompletingId] = useState<number | null>(null);

  const load = useCallback(async (silent = false) => {
    try {
      setOrders(await api.get<Order[]>("/api/orders/open"));
      setError(null);
      notifyOpenOrdersChanged();
    } catch (err) {
      if (!silent) setError(err instanceof ApiError ? err.message : "Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => load(true), 60_000);
    return () => clearInterval(interval);
  }, [load]);

  async function handleComplete(o: Order) {
    if (!confirm(`Bestellung #${o.id} von ${o.orderer_name} (${formatPrice(o.total)}) abschließen?\n\nSie wird dabei endgültig gelöscht (auch beim Besteller).`)) return;
    setCompletingId(o.id);
    setError(null);
    try {
      await api.post(`/api/orders/${o.id}/complete`, {});
      setOrders((list) => list.filter((x) => x.id !== o.id));
      notifyOpenOrdersChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Abschließen fehlgeschlagen.");
      await load(true);
    } finally {
      setCompletingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">Neue Bestellungen</h1>
          <p className="text-sm text-gray-500 mt-1">
            Eingegangene Bestellungen - mit dem Abschließen werden sie gelöscht.
          </p>
        </div>
        <button className="btn-secondary" onClick={() => load()}>🔄 Aktualisieren</button>
      </div>

      {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

      {loading ? (
        <Spinner />
      ) : orders.length === 0 ? (
        <Card className="p-6 text-center text-gray-500 text-sm">Keine offenen Bestellungen.</Card>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
          {orders.map((o) => (
            <Card key={o.id} padded={false} className="overflow-hidden">
              <div className="px-4 py-3 border-b border-surface-border flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm text-gray-100 font-semibold">Bestellung #{o.id} · {o.orderer_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{formatDateTime(o.created_at)}</p>
                </div>
                <p className="text-lg text-gray-100 font-semibold whitespace-nowrap">{formatPrice(o.total)}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="table-base">
                  <thead><tr><th>Artikel</th><th>Menge</th><th>Einzelpreis</th><th>Summe</th></tr></thead>
                  <tbody>
                    {o.items.map((i, idx) => (
                      <tr key={idx}>
                        <td className="text-gray-200">{i.item_name}</td>
                        <td className="text-gray-100 whitespace-nowrap">{i.quantity}×</td>
                        <td className="text-gray-400 whitespace-nowrap">{formatPrice(i.unit_price)}</td>
                        <td className="text-gray-100 font-medium whitespace-nowrap">{formatPrice(i.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-surface-border flex justify-end">
                <button className="btn-primary" disabled={completingId !== null} onClick={() => handleComplete(o)}>
                  {completingId === o.id ? "Wird abgeschlossen…" : "Bestellung abschließen"}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
